/**
 * @file ChangeCustomerModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-07
 * @modified 2026-10-15
 * @description "Change customer…" on a till invoice (HW-D…) or till credit note
 *              (CN-D…) — the one modal behind the action in Till Sales, Search
 *              Invoices and the invoice window.
 *
 *              A till sale sometimes goes through Epos Now as "POS Customer"
 *              when it should have been booked to a known customer's account.
 *              Only the customer moves (here and in Xero); lines, amounts, VAT,
 *              rep, reference, date and number stay as the till recorded them.
 *              The server decides everything (services/tillCustomerChangeService):
 *              this window shows why a document cannot be changed (paid,
 *              locked, voided…), the changes made so far, a SearchableDropdown
 *              of active customers and a required one-line reason, and shows
 *              whatever the server or Xero answers.
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import usePriorMonthConfirm from '../hooks/usePriorMonthConfirm';
import { describeCustomerChange } from '../utils/customerChange';
import './ChangeCustomerModal.css';

/*
 * The body. Its own component so useSlideOutButton mounts with the footer
 * buttons, not with whichever page opened the window (CLAUDE.md, Modal conventions).
 */
function ChangeCustomerBody({ context, loadError, accountId, setAccountId, reason, setReason, busy, error }) {
  useSlideOutButton([!!context, busy, !!accountId, !!reason.trim()]);
  if (loadError) return <p className="change-customer-error" role="alert">{loadError}</p>;
  if (!context) return <LoadingSpinner />;
  const inv = context.invoice;
  const label = inv.is_credit ? 'credit note' : 'invoice';
  const current = inv.customer || {};
  return (
    <>
      <p className="change-customer-intro">
        Moves this till {label} to another customer{inv.in_xero ? ', here and in Xero' : ''}. Nothing else changes:
        the lines, amounts, VAT, rep, reference, date and number stay as the till recorded them.
      </p>
      <div className="change-customer-summary">
        <span className="material-symbols-rounded">point_of_sale</span>
        <span className="summary-no">{inv.invoice_no}</span>
        <span className="summary-customer">
          {current.account_no ? `${current.account_no} · ` : ''}{current.name || 'No customer'}
        </span>
      </div>

      {context.history.length > 0 && (
        <ul className="change-customer-history" aria-label="Customer changes so far">
          {context.history.slice().reverse().map((h, i) => (
            <li key={`${h.at}-${i}`}>
              <span className="material-symbols-rounded">swap_horiz</span>
              <span>{describeCustomerChange(h)}</span>
            </li>
          ))}
        </ul>
      )}

      {context.block ? (
        <p className="change-customer-refusal" role="alert">
          <span className="material-symbols-rounded">block</span>
          <span>{context.block}</span>
        </p>
      ) : (
        <>
          <div className="change-customer-field">
            <label>New customer</label>
            <SearchableDropdown
              value={accountId}
              onChange={(v) => setAccountId(v || '')}
              disabled={busy}
              options={[
                { value: '', label: 'Choose a customer…' },
                ...context.customers.map(c => ({
                  value: String(c.id),
                  badge: c.account_no || '',
                  label: c.name,
                  searchText: `${c.account_no || ''} ${c.name}`
                }))
              ]}
              placeholder="Search by account or name..."
            />
          </div>
          <div className="change-customer-field">
            <label htmlFor="change-customer-reason">Reason</label>
            <Input
              id="change-customer-reason"
              aria-label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="One line — kept in its history here and in Xero"
              maxLength={500}
              disabled={busy}
            />
          </div>
        </>
      )}
      {error && <p className="change-customer-error" role="alert">{error}</p>}
    </>
  );
}

/**
 * @param {Object}   props
 * @param {number}   props.invoiceId  invoicing.invoices id of the till document
 * @param {Function} props.onClose
 * @param {Function} props.onDone     called with the server's answer once changed
 */
function ChangeCustomerModal({ invoiceId, onClose, onDone }) {
  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [accountId, setAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Dated in a month that should be locked in Xero by now: asked (Cam,
  // 15 Oct 2026); Save anyway sends it again, Go back changes nothing.
  const priorMonth = usePriorMonthConfirm();

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/change-customer`, { credentials: 'include' });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Could not open this document');
        if (live) setContext({ ...data, history: data.history || [], customers: data.customers || [] });
      } catch (e) {
        if (live) setLoadError(e.message);
      }
    })();
    return () => { live = false; };
  }, [invoiceId]);

  const canSave = !!context && !context.block && !!accountId && !!reason.trim() && !busy;

  const submit = async () => {
    if (!canSave) return;
    setBusy(true);
    setError('');
    try {
      const sent = await priorMonth.run((extra) => fetch(`${API_URL}/api/invoices/${invoiceId}/change-customer`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: Number(accountId), reason: reason.trim(), ...extra })
      }));
      if (sent.declined) return;
      const { res } = sent;
      const data = sent.data || {};
      if (!res.ok || !data.success) throw new Error(data.error || 'The customer could not be changed');
      onDone(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onClose}
      title={context ? `Change customer — ${context.invoice.invoice_no}` : 'Change customer'}
      className="change-customer"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={busy} data-read-safe>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">{context && context.block ? 'Close' : 'Cancel'}</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout"
              data-write-action
              onClick={submit}
              disabled={!canSave}
              title={context && context.block ? context.block
                : !accountId ? 'Choose the new customer'
                : !reason.trim() ? 'Give a reason' : undefined}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">swap_horiz</span></div>
              <span className="btn-label">{busy ? 'Changing…' : 'Change Customer'}</span>
            </button>
          </div>
        </div>
      }
    >
      <ChangeCustomerBody
        context={context}
        loadError={loadError}
        accountId={accountId}
        setAccountId={setAccountId}
        reason={reason}
        setReason={setReason}
        busy={busy}
        error={error}
      />
      {priorMonth.dialog}
    </Modal>
  );
}

export default ChangeCustomerModal;
